import { computed, inject, Injectable, signal } from '@angular/core';
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import {
  ToDosApiService,
  ToDoList,
  CreateListPayload,
  UpdateListPayload,
  AddTaskPayload,
  UpdateTaskPayload,
  AddGroupPayload,
  ListSettingsPayload,
  CopyListPayload,
  TaskCategoryOption,
} from './to-dos-api.service';
import { RoomModel } from 'src/models/to-do.model';

export const toDoKeys = {
  all: ['toDos'] as const,
  lists: () => [...toDoKeys.all, 'list'] as const,
  listsByAddress: (addressId: number) => [...toDoKeys.lists(), addressId] as const,
  orphanedLists: () => [...toDoKeys.lists(), 'orphaned'] as const,
  details: () => [...toDoKeys.all, 'detail'] as const,
  detail: (listId: number) => [...toDoKeys.details(), listId] as const,
  categoryOptions: () => [...toDoKeys.all, 'categoryOptions'] as const,
  appliedJobs: (listId: number) => [...toDoKeys.all, 'appliedJobs', listId] as const,
  jobToDos: (jobId: number) => [...toDoKeys.all, 'jobToDos', jobId] as const,
};

@Injectable({ providedIn: 'root' })
export class ToDosQueriesService {
  private readonly api = inject(ToDosApiService);
  private readonly queryClient = injectQueryClient();

  // State signals
  readonly selectedAddressId = signal<number | null>(null);
  readonly selectedListId = signal<number | null>(null);

  // Version signal - increments when lists are invalidated, allowing non-TanStack pages to react
  readonly listsVersion = signal(0);

  // Lists Query - fetches all lists for selected address
  readonly listsQuery = injectQuery(() => ({
    queryKey: toDoKeys.listsByAddress(this.selectedAddressId() ?? 0),
    queryFn: () => lastValueFrom(this.api.getListsForAddress(this.selectedAddressId()!)),
    enabled: this.selectedAddressId() !== null,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  }));

  readonly lists = computed(() => this.listsQuery.data() ?? []);
  readonly listsLoading = computed(() => this.listsQuery.isPending());
  readonly listsFetching = computed(() => this.listsQuery.isFetching());
  readonly listsError = computed(() => this.listsQuery.error());

  // List Detail Query - fetches rooms/tasks/notes/photos for selected list
  readonly listDetailQuery = injectQuery(() => ({
    queryKey: toDoKeys.detail(this.selectedListId() ?? 0),
    queryFn: () => lastValueFrom(this.api.getListDetail(this.selectedListId()!)),
    enabled: this.selectedListId() !== null,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  }));

  readonly listDetail = computed(() => this.listDetailQuery.data() ?? []);
  readonly listDetailLoading = computed(() => this.listDetailQuery.isPending());
  readonly listDetailFetching = computed(() => this.listDetailQuery.isFetching());
  readonly listDetailError = computed(() => this.listDetailQuery.error());

  // Category Options Query
  readonly categoryOptionsQuery = injectQuery(() => ({
    queryKey: toDoKeys.categoryOptions(),
    queryFn: () => lastValueFrom(this.api.getTaskCategoryOptions()),
    staleTime: 30 * 60 * 1000, // 30 minutes - these don't change often
    gcTime: 60 * 60 * 1000,
  }));

  readonly categoryOptions = computed(() => this.categoryOptionsQuery.data() ?? []);

  // Applied Jobs Query
  readonly appliedJobsQuery = injectQuery(() => ({
    queryKey: toDoKeys.appliedJobs(this.selectedListId() ?? 0),
    queryFn: () => lastValueFrom(this.api.getAppliedJobs(this.selectedListId()!)),
    enabled: this.selectedListId() !== null,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  }));

  readonly appliedJobs = computed(() => this.appliedJobsQuery.data() ?? []);

  // ========== List Mutations ==========

  readonly createListMutation = injectMutation(() => ({
    mutationFn: (payload: CreateListPayload) => lastValueFrom(this.api.createList(payload)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.listsByAddress(variables.address_id) });
    },
  }));

  readonly updateListMutation = injectMutation(() => ({
    mutationFn: ({ listId, payload }: { listId: number; payload: UpdateListPayload }) =>
      lastValueFrom(this.api.updateList(listId, payload)),
    onSuccess: () => {
      if (this.selectedAddressId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.listsByAddress(this.selectedAddressId()!) });
      }
      if (this.selectedListId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(this.selectedListId()!) });
      }
    },
  }));

  readonly deleteListMutation = injectMutation(() => ({
    mutationFn: (listId: number) => lastValueFrom(this.api.deleteList(listId)),
    onSuccess: () => {
      if (this.selectedAddressId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.listsByAddress(this.selectedAddressId()!) });
      }
    },
  }));

  readonly copyListMutation = injectMutation(() => ({
    mutationFn: ({ fromAddressId, listId, payload }: { fromAddressId: number; listId: number; payload: CopyListPayload }) =>
      lastValueFrom(this.api.copyList(fromAddressId, listId, payload)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.listsByAddress(variables.payload.target_address_id) });
    },
  }));

  readonly updateDefaultListMutation = injectMutation(() => ({
    mutationFn: ({ addressId, listId }: { addressId: number; listId: number }) =>
      lastValueFrom(this.api.updateDefaultList(addressId, listId)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.listsByAddress(variables.addressId) });
    },
  }));

  readonly saveListSettingsMutation = injectMutation(() => ({
    mutationFn: ({ listId, payload }: { listId: number; payload: ListSettingsPayload }) =>
      lastValueFrom(this.api.saveListSettings(listId, payload)),
    onSuccess: () => {
      if (this.selectedListId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(this.selectedListId()!) });
      }
      if (this.selectedAddressId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.listsByAddress(this.selectedAddressId()!) });
      }
    },
  }));

  // ========== Task Mutations ==========

  readonly addTaskMutation = injectMutation(() => ({
    mutationFn: ({ addressId, listId, payload }: { addressId: number; listId: number; payload: AddTaskPayload }) =>
      lastValueFrom(this.api.addTask(addressId, listId, payload)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(variables.listId) });
    },
  }));

  readonly updateTaskMutation = injectMutation(() => ({
    mutationFn: ({ addressId, listId, taskId, payload }: { addressId: number; listId: number; taskId: number; payload: UpdateTaskPayload }) =>
      lastValueFrom(this.api.updateTask(addressId, listId, taskId, payload)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(variables.listId) });
    },
  }));

  readonly deleteTaskMutation = injectMutation(() => ({
    mutationFn: ({ addressId, listId, taskId }: { addressId: number; listId: number; taskId: number }) =>
      lastValueFrom(this.api.deleteTask(addressId, listId, taskId)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(variables.listId) });
    },
  }));

  readonly updateTaskOrderMutation = injectMutation(() => ({
    mutationFn: ({ listId, groupId, taskIds }: { listId: number; groupId: number; taskIds: number[] }) =>
      lastValueFrom(this.api.updateTaskOrder(listId, { address_task_group_id: groupId, task_ids_in_order: taskIds })),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(variables.listId) });
    },
  }));

  // ========== Group/Room Mutations ==========

  readonly addGroupMutation = injectMutation(() => ({
    mutationFn: async ({ payload, listId }: { payload: AddGroupPayload; listId: number }) => {
      const group = await lastValueFrom(this.api.addGroup(payload));
      await lastValueFrom(this.api.addGroupToList(group.id, { address_task_list_id: listId }));
      return group;
    },
    onSuccess: () => {
      if (this.selectedListId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(this.selectedListId()!) });
      }
    },
  }));

  readonly updateGroupNameMutation = injectMutation(() => ({
    mutationFn: ({ groupId, name }: { groupId: number; name: string }) =>
      lastValueFrom(this.api.updateGroupName(groupId, { name })),
    onSuccess: () => {
      if (this.selectedListId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(this.selectedListId()!) });
      }
    },
  }));

  readonly deleteGroupMutation = injectMutation(() => ({
    mutationFn: ({ groupId, listId }: { groupId: number; listId: number }) =>
      lastValueFrom(this.api.removeGroupFromList(groupId, listId)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(variables.listId) });
    },
  }));

  readonly updateGroupPriorityMutation = injectMutation(() => ({
    mutationFn: ({ listId, orderedGroupIds }: { listId: number; orderedGroupIds: number[] }) =>
      lastValueFrom(this.api.updateGroupPriority(listId, orderedGroupIds)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(variables.listId) });
    },
  }));

  readonly setItemsOrderMutation = injectMutation(() => ({
    mutationFn: ({ groupId, items }: { groupId: number; items: { type: string; id: number }[]; listId?: number }) =>
      lastValueFrom(this.api.setItemsDisplayOrder(groupId, items)),
    onSuccess: async (_, variables) => {
      // Refetch using explicit listId if provided, otherwise use selectedListId
      // Using refetchQueries instead of invalidateQueries to avoid double-fetching
      const listIdToRefetch = variables.listId ?? this.selectedListId();
      if (listIdToRefetch) {
        await this.queryClient.refetchQueries({ queryKey: toDoKeys.detail(listIdToRefetch) });
      }
    },
  }));

  // ========== Notes Mutations ==========

  readonly addTextNoteMutation = injectMutation(() => ({
    mutationFn: ({ groupId, body }: { groupId: number; body: string }) =>
      lastValueFrom(this.api.addTextNote({
        text_notes: [{ type: 'address_task_group', object_id: groupId, body }],
      })),
    onSuccess: () => {
      if (this.selectedListId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(this.selectedListId()!) });
      }
    },
  }));

  readonly updateTextNoteMutation = injectMutation(() => ({
    mutationFn: ({ noteId, body }: { noteId: number; body: string }) =>
      lastValueFrom(this.api.updateTextNote(noteId, body)),
    onSuccess: () => {
      if (this.selectedListId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(this.selectedListId()!) });
      }
    },
  }));

  readonly deleteTextNoteMutation = injectMutation(() => ({
    mutationFn: (noteId: number) => lastValueFrom(this.api.deleteTextNote(noteId)),
    onSuccess: () => {
      if (this.selectedListId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(this.selectedListId()!) });
      }
    },
  }));

  // ========== Photo Mutations ==========

  readonly addPhotoNoteMutation = injectMutation(() => ({
    mutationFn: ({ type, objectId, photoUrl, caption }: { type: 'task' | 'group'; objectId: number; photoUrl: string; caption?: string }) =>
      lastValueFrom(this.api.addPhotoNote({
        photo_notes: [{ type, object_id: objectId, photo_url: photoUrl, caption }],
      })),
    onSuccess: () => {
      if (this.selectedListId()) {
        this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(this.selectedListId()!) });
      }
    },
  }));

  // ========== Job Mutations ==========

  readonly assignListToJobsMutation = injectMutation(() => ({
    mutationFn: ({ jobIds, listId }: { jobIds: number[]; listId: number }) =>
      lastValueFrom(this.api.assignListToJobs(jobIds, listId)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.appliedJobs(variables.listId) });
    },
  }));

  readonly updateJobToDosMutation = injectMutation(() => ({
    mutationFn: ({ jobId, payload }: { jobId: number; payload: any }) =>
      lastValueFrom(this.api.updateJobToDos(jobId, payload)),
    onSuccess: (_, variables) => {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.jobToDos(variables.jobId) });
    },
  }));

  // ========== Helper Methods ==========

  selectAddress(addressId: number | null): void {
    this.selectedAddressId.set(addressId);
  }

  selectList(listId: number | null): void {
    this.selectedListId.set(listId);
  }

  invalidateLists(addressId?: number): void {
    const id = addressId ?? this.selectedAddressId();
    if (id) {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.listsByAddress(id) });
    }
    // Increment version to notify non-TanStack pages (like AllToDoListsPage)
    this.listsVersion.update(v => v + 1);
  }

  invalidateListDetail(listId?: number): void {
    const id = listId ?? this.selectedListId();
    if (id) {
      this.queryClient.invalidateQueries({ queryKey: toDoKeys.detail(id) });
    }
  }

  async refetchLists(): Promise<void> {
    await this.listsQuery.refetch();
  }

  async refetchListDetail(): Promise<RoomModel[]> {
    const result = await this.listDetailQuery.refetch();
    return result.data ?? [];
  }

  // Legacy compatibility: Get lists for an address (Promise-based)
  // Uses fetchQuery to respect cache invalidation - will refetch if stale/invalidated
  async getListsForAddress(addressId: number): Promise<ToDoList[]> {
    const cacheKey = toDoKeys.listsByAddress(addressId);
    return this.queryClient.fetchQuery({
      queryKey: cacheKey,
      queryFn: () => lastValueFrom(this.api.getListsForAddress(addressId)),
      staleTime: 5 * 60 * 1000,
    });
  }

  async getOrphanedLists(): Promise<ToDoList[]> {
    return this.queryClient.fetchQuery({
      queryKey: toDoKeys.orphanedLists(),
      queryFn: () => lastValueFrom(this.api.getOrphanedLists()),
      staleTime: 5 * 60 * 1000,
    });
  }

  // Legacy compatibility: Get list detail (Promise-based)
  // Uses fetchQuery to respect cache invalidation - will refetch if stale/invalidated
  async getListDetail(listId: number): Promise<RoomModel[]> {
    const cacheKey = toDoKeys.detail(listId);
    return this.queryClient.fetchQuery({
      queryKey: cacheKey,
      queryFn: () => lastValueFrom(this.api.getListDetail(listId)),
      staleTime: 5 * 60 * 1000,
    });
  }

  // Legacy compatibility: Get category options (Promise-based)
  // Uses fetchQuery to respect cache invalidation - will refetch if stale/invalidated
  async getCategoryOptions(): Promise<TaskCategoryOption[]> {
    const cacheKey = toDoKeys.categoryOptions();
    return this.queryClient.fetchQuery({
      queryKey: cacheKey,
      queryFn: () => lastValueFrom(this.api.getTaskCategoryOptions()),
      staleTime: 30 * 60 * 1000,
    });
  }

  // Get job to-dos (Promise-based, not cached heavily since it's job-specific)
  async getJobToDos(jobId: number): Promise<any> {
    return lastValueFrom(this.api.getToDosForJob(jobId));
  }

  // Find a list by ID from cached lists
  getListById(listId: number): ToDoList | undefined {
    return this.lists().find((list) => list.id === listId);
  }
}
